import { useNetwork } from '@/contexts/NetworkContext';
import { WifiOff, CloudOff, RefreshCw, Clock } from 'lucide-react';
import { Button3D } from '@/components/ui/button-3d';
import { cn } from '@/lib/utils';

interface OfflineIndicatorProps {
  className?: string;
  showPendingCount?: boolean;
}

export function OfflineIndicator({ className, showPendingCount = true }: OfflineIndicatorProps) {
  const { isOnline, isSyncing, pendingOperations, syncNow } = useNetwork();

  // Don't show anything if online and no pending operations
  if (isOnline && pendingOperations === 0 && !isSyncing) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        !isOnline ? 'bg-destructive' : 'bg-warning',
        className
      )}
    >
      <div className="max-w-2xl mx-auto px-4 py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-white text-sm">
          {!isOnline ? (
            <>
              <WifiOff className="h-4 w-4 flex-shrink-0" />
              <span>Sem conexão</span>
            </>
          ) : isSyncing ? (
            <>
              <RefreshCw className="h-4 w-4 flex-shrink-0 animate-spin" />
              <span>Sincronizando...</span>
            </>
          ) : (
            <>
              <Clock className="h-4 w-4 flex-shrink-0" />
              <span>
                {pendingOperations} pendente{pendingOperations > 1 ? 's' : ''}
              </span>
            </>
          )}
        </div>

        {isOnline && pendingOperations > 0 && !isSyncing && (
          <Button3D
            color="green"
            size="sm"
            onClick={syncNow}
          >
            Sincronizar
          </Button3D>
        )}
      </div>
    </div>
  );
}

// Badge version for use in navigation/headers
export function OfflineBadge({ className }: { className?: string }) {
  const { isOnline, pendingOperations } = useNetwork();

  if (isOnline && pendingOperations === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
        !isOnline 
          ? 'bg-destructive/10 text-destructive'
          // `text-warning-foreground` é BRANCO PURO (`--warning-foreground:
          // 0 0% 100%`), feito para ficar sobre o âmbar SÓLIDO. Sobre este
          // fundo translúcido ele dava contraste **1,21** no tema claro —
          // branco sobre âmbar pálido, ilegível. Medido no browser; jsdom não
          // enxerga cor, então a suíte nunca veria. Ver o hurdle #207.
          //
          // ⚠️ `OfflineBadge` NÃO É USADO por ninguém hoje (só o
          // `OfflineIndicator` acima, via `MainLayout`), e por isso o
          // tree-shaking o tira do bundle — provado no aparelho, procurando
          // esta classe no JS instalado. A correção fica porque o defeito é
          // real no instante em que alguém montar este componente; o que NÃO
          // se pode dizer é que ela consertou algo que o usuário via.
          : 'bg-warning/20 text-amber-700 dark:text-amber-400',
        className
      )}
    >
      {!isOnline ? (
        <>
          <CloudOff className="h-3 w-3" />
          <span>Offline</span>
        </>
      ) : (
        <>
          <Clock className="h-3 w-3" />
          <span>{pendingOperations}</span>
        </>
      )}
    </div>
  );
}
